import React, {
    useMemo,
    useRef,
    useCallback,
    useState,
    useEffect,
} from "react";
import {
    TransactionCategoriesView,
    TransactionCategoriesViewSchema,
    TransactionCategoryColumn,
} from "@common/types/views/transactionCategoriesView";
import { View, ViewType, ViewDensity } from "@common/types/views/views";
import { FiltersTypes } from "@common/types/filters/general";
import { ServerInferResponseBody } from "@ts-rest/core";
import { categoryContract } from "@common/contracts/category.contract";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { useBusinessEntityIds } from "@hooks/useBusinessEntityIds";
import { ExpandedState } from "@tanstack/react-table";
import { useCategoriesTree } from "@hooks/useCategoriesTree";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { CategoriesHeader } from "./children/CategoriesHeader";
import { CategoriesStats } from "./CategoriesStats";
import { createCategoriesViewConfig } from "./categoriesViewConfig";
import { useCategoryStatistics } from "./useCategoryStatistics";
import {
    useCategoryGlMappings,
    buildCategoryGlMappingsMap,
} from "./useCategoryGlMappings";
import {
    buildTransactionCategoryRows,
    getCategoryDefaultExpandedState,
    BuildCategoryRowsOptions,
    CategoryStatMap,
} from "./viewUtils";

type CategoryStat = ServerInferResponseBody<
    typeof categoryContract.getCategoryStatistics
>[number];

function buildDefaultView({
    isGlMappingColumnEnabled,
}: {
    isGlMappingColumnEnabled: boolean;
}): TransactionCategoriesView {
    return {
        type: ViewType.TRANSACTION_CATEGORIES,
        columns: [
            { id: TransactionCategoryColumn.NAME },
            { id: TransactionCategoryColumn.DESCRIPTION },
            ...(isGlMappingColumnEnabled
                ? [{ id: TransactionCategoryColumn.GL_MAPPING }]
                : []),
            { id: TransactionCategoryColumn.PERCENTAGE },
            { id: TransactionCategoryColumn.TRANSACTIONS },
            {
                id: TransactionCategoryColumn.ACTIONS,
                options: { pin: "right" },
            },
        ],
        filters: {},
        options: { density: ViewDensity.MD },
    };
}

interface Props {
    closeSettingsModal?: (disableAnimation?: boolean) => void;
}

export const Categories: React.FC<Props> = () => {
    const categories = useCategoriesTree();
    const businessEntityIds = useBusinessEntityIds();

    const { categoryGlMapping } = useTypedFlags();
    const { enabled: isGlFirst } = useIsGlFirst();
    const isGlMappingColumnEnabled = categoryGlMapping && !isGlFirst;

    const viewConfig = useMemo(() => createCategoriesViewConfig(), []);

    const defaultView = useMemo(
        () => buildDefaultView({ isGlMappingColumnEnabled }),
        [isGlMappingColumnEnabled],
    );

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<
        Pick<FiltersTypes, "end" | "start" | "entityIds" | "category">
    >({ entityIds: businessEntityIds });

    const stats = useCategoryStatistics(filters);
    const glMappings = useCategoryGlMappings({ isGlMappingColumnEnabled });

    const glMappingsMap = useMemo(
        () => buildCategoryGlMappingsMap(glMappings.data?.body ?? []),
        [glMappings.data?.body],
    );

    const statsMap = useMemo(
        () =>
            new Map<string, CategoryStatMap>(
                (stats.data?.body ?? []).map((stat) => [
                    stat.id,
                    { count: stat.count, groupCount: stat.groupCount },
                ]),
            ),
        [stats.data?.body],
    );

    const categoriesStatsMap = useMemo(
        () =>
            new Map<string, CategoryStat>(
                (stats.data?.body ?? []).map((stat) => [stat.id, stat]),
            ),
        [stats.data?.body],
    );

    const total = useMemo(
        () =>
            (stats.data?.body ?? []).reduce(
                (acc, stat) =>
                    !stat.parentCategoryId ? acc + stat.count : acc,
                0,
            ),
        [stats.data?.body],
    );

    const buildOptions = useMemo<BuildCategoryRowsOptions>(
        () => ({ search, filters, statsMap, total, glMappingsMap }),
        [search, filters, statsMap, total, glMappingsMap],
    );

    const rows = useMemo(
        () => buildTransactionCategoryRows(categories, buildOptions),
        [categories, buildOptions],
    );

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<TransactionCategoriesView>(
            WORKSPACE_USER_CACHE_KEYS.TRANSACTION_CATEGORIES_PAGE_VIEW_CACHE_KEY,
            TransactionCategoriesViewSchema,
        );

    const [view, setView] = useState<TransactionCategoriesView>(
        cachedView ?? defaultView,
    );

    const effectiveView = useMemo<TransactionCategoriesView>(() => {
        const hasGlMappingColumn = view.columns.some(
            (column) => column.id === TransactionCategoryColumn.GL_MAPPING,
        );

        if (!isGlMappingColumnEnabled) {
            return hasGlMappingColumn
                ? {
                      ...view,
                      columns: view.columns.filter(
                          (column) =>
                              column.id !==
                              TransactionCategoryColumn.GL_MAPPING,
                      ),
                  }
                : view;
        }

        if (hasGlMappingColumn) {
            return view;
        }

        const descriptionIndex = view.columns.findIndex(
            (column) => column.id === TransactionCategoryColumn.DESCRIPTION,
        );
        const insertAt = descriptionIndex === -1 ? 1 : descriptionIndex + 1;
        return {
            ...view,
            columns: [
                ...view.columns.slice(0, insertAt),
                { id: TransactionCategoryColumn.GL_MAPPING },
                ...view.columns.slice(insertAt),
            ],
        };
    }, [view, isGlMappingColumnEnabled]);

    const handleViewChange = useCallback(
        (newView: View) => {
            setView(newView as TransactionCategoriesView);
            setCachedView(newView as TransactionCategoriesView);
        },
        [setCachedView],
    );

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);
    const hasInitializedExpandedRef = useRef(false);

    useEffect(() => {
        if (hasInitializedExpandedRef.current || rows.length === 0) {
            return;
        }
        hasInitializedExpandedRef.current = true;
        setExpanded(getCategoryDefaultExpandedState(rows));
    }, [rows]);

    const handleToggleCategory = useCallback(
        (val: string) => {
            const parentCategoryId =
                rows.find((row) => row.id === val)?.category
                    ?.parentCategoryId ?? val;

            if (filters.category?.includes(parentCategoryId)) {
                setFilters((prev) => ({ ...prev, category: undefined }));
            } else {
                setFilters((prev) => ({
                    ...prev,
                    category: [parentCategoryId],
                }));
            }
        },
        [rows, filters, setFilters],
    );

    const tableMeta = useMemo(
        () => ({ entityIds: filters.entityIds }),
        [filters.entityIds],
    );

    return (
        <main>
            <CategoriesHeader
                expanded={expanded}
                tableRef={tableRef}
                search={search}
                setSearch={setSearch}
                filters={filters}
                onFiltersChange={(newFilters) =>
                    setFilters((prev) => ({ ...prev, ...newFilters }))
                }
            />

            {rows.length > 0 && total > 0 && (
                <CategoriesStats
                    statsMap={categoriesStatsMap}
                    stats={stats.data?.body ?? []}
                    total={total}
                    categories={rows.flatMap((r) => [r, ...(r.children ?? [])])}
                    toggleCategory={handleToggleCategory}
                />
            )}

            <ViewTable
                ref={tableRef}
                data={rows}
                view={effectiveView}
                config={viewConfig}
                onViewChange={handleViewChange}
                expanded={expanded}
                onExpandedChange={setExpanded}
                rowBorders={false}
                isParentSelectable={false}
                fixedTopOffsetPx
                recomputeTopOffsetOn={[stats.data?.body]}
                tableMeta={tableMeta}
            />
        </main>
    );
};
